import { useLayoutEffect, useRef } from "react"
import { useAppDispatch, useAppSelector } from "@/store/hooks"
import { setModalData } from "@/store/globalSlice"
import Money from "@/Components/Money"
import { allMapsImageData, allPieces, images } from "@/utils/data"
import classNames from "classnames"
import { gameRoles } from "@/utils/gameRoles"
import PropertyBigCard from "@/Components/PropertyBigCard"
import { getMapById } from "@/utils/gameHelpers"
import ModalCloseButton from "@/Components/ModalCloseButton"
import { MODAL_SHELL_CLASS, ModalTopAccent } from "@/Components/Modal"
import { getPlayerAvatar } from "@/utils/playerAvatar"
const tempUserImage =
  "https://plus.unsplash.com/premium_photo-1671656349322-41de944d259b?fm=jpg&q=60&w=3000&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MXx8dXNlcnxlbnwwfHwwfHx8MA%3D%3D"
const AuctionResult: React.FC = () => {
  const dispatch = useAppDispatch()
  const myGame = useAppSelector((state) => state.global.myGame)
  const modalData = useAppSelector((state) => state.global.modalData)
  const players = myGame?.situation?.players || []
  const users = myGame?.players || []
  const myMap = getMapById(myGame?.settings.mapId || "")

  // Find highest bidder
  const auctionOrder = modalData.auctionResult?.propertyOrder
  const propertyOrderToShow =
    auctionOrder ?? myGame?.situation?.activeAuctionPropertyOrder

  // Source bids from the matching history item (more reliable than situation.auctionBids which gets cleared)
  const historyItems = myGame?.situation?.historyItems || []
  const auctionHistoryItem = [...historyItems]
    .reverse()
    .find(
      (item): item is IHistoryAuctionWon | IHistoryAuctionTied =>
        (item.type === "auction-won" || item.type === "auction-tied") &&
        item.propertyOrder === propertyOrderToShow
    )
  const auctionBids: Record<number, number> =
    auctionHistoryItem?.auctionBids ?? myGame?.situation?.auctionBids ?? {}

  const bidValues = Object.values(auctionBids).filter(
    (bid): bid is number => typeof bid === "number"
  )
  const highestBid = bidValues.length > 0 ? Math.max(...bidValues) : 0
  // Compute second highest bid for a subtle winner margin indicator
  const sortedBidsDesc = [...bidValues].sort((a, b) => b - a)
  const secondHighestBid = sortedBidsDesc.length > 1 ? sortedBidsDesc[1] : 0
  const highestBidderOrder = Object.entries(auctionBids).find(
    ([_, bid]) => bid === highestBid
  )?.[0]

  // Check if auction is tied (multiple players with same highest bid)
  const tiedBidders = Object.entries(auctionBids).filter(
    ([_, bid]) => bid === highestBid && highestBid > 0
  )
  const isAuctionTied = tiedBidders.length > 1

  // --- Minimize/restore animation -----------------------------------------
  // The modal morphs to/from the sidebar's "Last Auction Results" button: the
  // dark shell animates onto the button's exact rect so it visually *becomes*
  // the button, while the inner content cross-fades separately (so squashed
  // text is never visible mid-flight). Driven with the Web Animations API so
  // open/close can't race React state updates.
  const modalRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)

  const prefersReducedMotion = () =>
    window.matchMedia("(prefers-reduced-motion: reduce)").matches

  const getButtonEl = () =>
    document.querySelector<HTMLElement>(
      '[data-testid="game.last-auction-result-button"]'
    )

  // Transform that lays the modal shell exactly over the button.
  const getMorphTransform = (): string | null => {
    const m = modalRef.current?.getBoundingClientRect()
    const b = getButtonEl()?.getBoundingClientRect()
    if (!m || !b || m.width === 0 || m.height === 0) return null
    const dx = b.left + b.width / 2 - (m.left + m.width / 2)
    const dy = b.top + b.height / 2 - (m.top + m.height / 2)
    const sx = b.width / m.width
    const sy = b.height / m.height
    return `translate(-50%, -50%) translate(${dx}px, ${dy}px) scale(${sx}, ${sy})`
  }

  // Grow the modal out of the button on mount.
  useLayoutEffect(() => {
    const modal = modalRef.current
    if (!modal || typeof modal.animate !== "function") return
    if (prefersReducedMotion()) return

    const from = getMorphTransform()
    if (!from) {
      // Button not on screen — gentle centered scale/fade instead.
      modal.animate(
        [
          { transform: "translate(-50%, -50%) scale(0.96)", opacity: 0 },
          { transform: "translate(-50%, -50%) scale(1)", opacity: 1 },
        ],
        { duration: 200, easing: "ease-out" }
      )
      return
    }

    modal.animate(
      [{ transform: from }, { transform: "translate(-50%, -50%)" }],
      { duration: 260, easing: "cubic-bezier(0.22, 1, 0.36, 1)" }
    )
    modal.animate([{ opacity: 0 }, { opacity: 1 }], {
      duration: 100,
      easing: "ease-out",
    })
    // Content waits for the shell to take shape, then fades in.
    contentRef.current?.animate([{ opacity: 0 }, { opacity: 1 }], {
      duration: 160,
      delay: 80,
      easing: "ease-out",
      fill: "backwards",
    })
    // Only run once, when the modal first mounts (i.e. becomes visible).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleClose = () => {
    const finish = () =>
      dispatch(
        setModalData({
          auctionResult: { visible: false, propertyOrder: undefined },
        })
      )

    const modal = modalRef.current
    if (!modal || typeof modal.animate !== "function") return finish()
    if (prefersReducedMotion()) return finish()

    // Ignore further clicks while the modal is flying to the button.
    modal.style.pointerEvents = "none"

    const to = getMorphTransform()
    if (!to) {
      const fallback = modal.animate(
        [
          { transform: "translate(-50%, -50%) scale(1)", opacity: 1 },
          { transform: "translate(-50%, -50%) scale(0.96)", opacity: 0 },
        ],
        { duration: 160, easing: "ease-in", fill: "forwards" }
      )
      fallback.onfinish = finish
      return
    }

    // Content vanishes first so only the clean shell makes the flight.
    contentRef.current?.animate([{ opacity: 1 }, { opacity: 0 }], {
      duration: 90,
      easing: "ease-out",
      fill: "forwards",
    })
    // The shell stays solid while it flies and only fades as it lands on the
    // button, so the eye can track where it went.
    const flight = modal.animate(
      [
        { transform: "translate(-50%, -50%)", opacity: 1 },
        { transform: to, opacity: 0.9, offset: 0.8 },
        { transform: to, opacity: 0 },
      ],
      {
        duration: 230,
        easing: "cubic-bezier(0.3, 0, 0.8, 0.15)",
        fill: "forwards",
      }
    )
    flight.onfinish = () => {
      // A small "catch" pulse on the button sells the landing.
      getButtonEl()?.animate(
        [
          { transform: "scale(1)" },
          { transform: "scale(1.12)" },
          { transform: "scale(1)" },
        ],
        { duration: 180, easing: "ease-out" }
      )
      finish()
    }
  }

  if (!modalData.auctionResult?.visible) return null
  if (modalData.rolePickingModal?.visible) return null

  return (
    <div
      ref={modalRef}
      className={classNames(
        MODAL_SHELL_CLASS,
        "fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[70%] md:w-[55%] h-fit z-[100] p-8"
      )}
    >
      <ModalTopAccent />
      <div ref={contentRef}>
        <div className="relative mb-12">
          <div className="flex items-center justify-center gap-3">
            <i className="fa-solid fa-gavel text-gray-300 text-2xl md:text-3xl"></i>
            <h2 className="font-poppins font-extrabold tracking-wide text-white/90 text-4xl md:text-5xl">
              Auction Results
            </h2>
          </div>
          <div className="absolute right-0 top-1/2 -translate-y-1/2">
            <ModalCloseButton onClick={handleClose} />
          </div>
        </div>

        <div className="grid grid-cols-[auto_1fr] items-start gap-12 md:gap-6">
          {myGame &&
            propertyOrderToShow !== undefined &&
            propertyOrderToShow !== null && (
              <div className="mb-0 md:justify-self-start justify-self-center">
                <PropertyBigCard
                  cell={
                    myMap?.cells.find(
                      (cell) => cell.order === propertyOrderToShow
                    ) as ICell
                  }
                  size="auction-modal"
                />
              </div>
            )}

          <div className="flex flex-col gap-3 md:gap-6">
            {players
              .filter((player) => !player.isBankrupt)
              .map((player) => {
                const user = users.find((u) => u.email === player.id)
                const piece = allPieces.find((p) => p.id === player.pieceId)
                const bid =
                  auctionBids[player.order as keyof typeof auctionBids] || 0
                const isHighestBidder =
                  !isAuctionTied &&
                  player.order.toString() === highestBidderOrder
                const isTiedHighBidder =
                  isAuctionTied && bid === highestBid && highestBid > 0
                const myAvatar = getPlayerAvatar(player, user)

                return (
                  <div
                    key={player.order}
                    className={classNames(
                      "border rounded-xl transition-all duration-300",
                      {
                        "p-5 bg-gray-800 border-gray-700 transform scale-[0.95] filter grayscale-[0.1] saturate-50":
                          !isHighestBidder && !isTiedHighBidder,
                        "p-5 bg-gray-800 border-gray-600 border-l-4 border-l-yellow-400/60":
                          isTiedHighBidder,
                        "p-5 md:p-8 relative overflow-hidden bg-gradient-to-br from-yellow-300/5 via-amber-200/5 to-transparent border-yellow-500/30 ring-1 ring-yellow-300/30 shadow-[0_0_24px_-8px_rgba(234,179,8,0.25)] scale-[1.04] border-l-4 ":
                          isHighestBidder,
                      }
                    )}
                  >
                    <div className="flex justify-between items-center">
                      <div className="flex min-w-0 items-center gap-4">
                        <img
                          src={piece?.imgSrc}
                          alt="Player piece"
                          className={classNames("rounded-md translate-y-0.5", {
                            // "h-16 w-16": isHighestBidder,
                            "h-12 w-12": !isHighestBidder,
                          })}
                        />
                        <div className="flex min-w-0 flex-col">
                          <div className="flex min-w-0 items-center gap-3">
                            <img
                              className={classNames(
                                "h-12 w-12 rounded-full object-cover",
                                {
                                  "ring-2 ring-yellow-400 ring-offset-2 ring-offset-black/50":
                                    isHighestBidder,
                                }
                              )}
                              src={
                                user?.profileImage
                                  ? encodeURI(user.profileImage)
                                  : myAvatar
                                  ? myAvatar
                                  : tempUserImage
                              }
                              alt="Player avatar"
                            />
                            <div className="flex min-w-0 flex-col">
                              <div className="flex min-w-0 items-center gap-2">
                                <span
                                  className={classNames(
                                    "min-w-0 max-w-[10rem] truncate font-poppins font-semibold text-white sm:max-w-[14rem]",
                                    {
                                      "text-2xl": isHighestBidder,
                                      "text-xl": !isHighestBidder,
                                    }
                                  )}
                                  title={user?.username}
                                >
                                  {user?.username}
                                </span>
                                {isHighestBidder && (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-yellow-400/20 text-yellow-300 text-xs font-poppins font-semibold">
                                    <i className="fa-solid fa-trophy"></i>
                                    Winner
                                  </span>
                                )}
                                {isTiedHighBidder && (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/10 text-yellow-200/90 text-xs font-poppins font-medium">
                                    <i className="fa-solid fa-scale-balanced"></i>
                                    Tied highest bid
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                {/* <span className="text-sm font-poppins text-gray-400">
                              Player {player.order}
                            </span> */}
                                {player.role !== "none" && (
                                  <span
                                    className="px-2 py-0.5 rounded-md text-xs font-medium font-poppins h-fit whitespace-nowrap"
                                    style={{
                                      backgroundColor:
                                        (gameRoles.find(
                                          (r) => r.id === player.role
                                        )?.color || "#888") + "40",
                                      color:
                                        gameRoles.find(
                                          (r) => r.id === player.role
                                        )?.color || "#fff",
                                    }}
                                  >
                                    {
                                      gameRoles.find(
                                        (r) => r.id === player.role
                                      )?.title
                                    }
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <div className="font-poppins w-full flex items-center justify-end gap-1">
                          <i className="fa-solid fa-gavel  text-white/80 "></i>
                          <span className="inline-flex items-center gap-2 text-base md:text-2xl text-white/80 font-semibold">
                            Bid
                          </span>
                        </div>
                        <Money
                          money={bid}
                          className={classNames(
                            "font-bold font-poppins gap-2",
                            {
                              "text-3xl text-white": !isHighestBidder,
                              "text-4xl text-yellow-400": isHighestBidder,
                            }
                          )}
                          iconClass={classNames({
                            "h-9": !isHighestBidder,
                            "h-10": isHighestBidder,
                          })}
                        />
                        {isHighestBidder && (
                          <div className="mt-1 text-xs md:text-sm text-yellow-300/80 font-poppins">
                            Highest bid
                            {secondHighestBid > 0 &&
                              highestBid - secondHighestBid > 0 && (
                                <span className="ml-2 text-yellow-200/80">
                                  (+{highestBid - secondHighestBid} ahead)
                                </span>
                              )}
                          </div>
                        )}
                      </div>
                    </div>
                    {/* {isHighestBidder &&
                    myMap &&
                    auctionOrder !== undefined &&
                    auctionOrder !== null && (
                      <div className="mt-3 pt-3 border-t border-yellow-500/20 text-yellow-100/80 text-xs md:text-sm font-poppins">
                        Acquired:{" "}
                        {(
                          myMap.cells.find(
                            (c) => c.order === auctionOrder
                          ) as ICell
                        )?.propertyDetails?.name || "Selected property"}
                      </div>
                    )} */}
                  </div>
                )
              })}
          </div>
        </div>
      </div>
    </div>
  )
}

export default AuctionResult
